import { Label } from "@measured/puck";
import React, { useEffect, useState } from "react";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { LucideProps } from "lucide-react";
import dynamicIconImports from "lucide-react/dynamicIconImports";

type DynamicIconProps = {
  name: string;
  size?: number;
  color?: string;
};

// Categorized icons for dashboard pages
const iconCategories = [
  {
    category: "General",
    icons: [
      { label: "Home", value: "home" },
      { label: "Dashboard", value: "layout-dashboard" },
    ],
  },
  {
    category: "Charts & Analytics",
    icons: [
      { label: "Bar Chart", value: "bar-chart" },
      { label: "Pie Chart", value: "pie-chart" },
      { label: "Line Chart", value: "line-chart" },
      { label: "Activity", value: "activity" },
      { label: "Trending Up", value: "trending-up" },
      { label: "Trending Down", value: "trending-down" },
      { label: "Target", value: "target" },
      { label: "Gauge", value: "gauge" },
    ],
  },
  {
    category: "Data & Content",
    icons: [
      { label: "Table", value: "table" },
      { label: "Database", value: "database" },
      { label: "File Text", value: "file-text" },
      { label: "Folder", value: "folder" },
      { label: "Archive", value: "archive" },
    ],
  },
  {
    category: "People & Users",
    icons: [
      { label: "User", value: "user" },
      { label: "Users", value: "users" },
    ],
  },
  {
    category: "Business & Finance",
    icons: [
      { label: "Briefcase", value: "briefcase" },
      { label: "Credit Card", value: "credit-card" },
      { label: "Wallet", value: "wallet" },
      { label: "Receipt", value: "receipt" },
      { label: "Coins", value: "coins" },
    ],
  },
  {
    category: "Operations",
    icons: [
      { label: "Package", value: "package" },
      { label: "Shopping Cart", value: "shopping-cart" },
      { label: "Truck", value: "truck" },
      { label: "Building", value: "building" },
      { label: "Factory", value: "factory" },
    ],
  },
  {
    category: "Communication",
    icons: [
      { label: "Mail", value: "mail" },
      { label: "Phone", value: "phone" },
      { label: "Message Square", value: "message-square" },
      { label: "Bell", value: "bell" },
    ],
  },
  {
    category: "Status & Actions",
    icons: [
      { label: "Check Circle", value: "check-circle" },
      { label: "Circle Check", value: "circle-check" },
      { label: "Circle X", value: "circle-x" },
      { label: "Alert Circle", value: "alert-circle" },
      { label: "Info", value: "info" },
      { label: "Star", value: "star" },
    ],
  },
  {
    category: "Time & Organization",
    icons: [
      { label: "Calendar", value: "calendar" },
      { label: "Clock", value: "clock" },
      { label: "Timer", value: "timer" },
      { label: "Tags", value: "tags" },
      { label: "Bookmark", value: "bookmark" },
    ],
  },
  {
    category: "Utilities",
    icons: [
      { label: "Settings", value: "settings" },
      { label: "Search", value: "search" },
      { label: "Download", value: "download" },
      { label: "Upload", value: "upload" },
      { label: "Globe", value: "globe" },
    ],
  },
];

// Flatten all icons for search
const allIcons = iconCategories.flatMap((cat) => cat.icons);

export const DynamicLucideIcon: React.FC<DynamicIconProps> = ({
  name,
  size = 18,
  color = "currentColor",
}) => {
  const [Icon, setIcon] = useState<any>(null);

  const loadIcon = async () => {
    if (name === "none" || !name) return;
    const icon = await dynamicIconImports[
      name as keyof typeof dynamicIconImports
    ]();
    setIcon(icon);
  };

  useEffect(() => {
    if (name === "none" || !name) return;
    loadIcon();
  }, [name]);

  if (name === "none" || !name) return null;

  // Create props object that matches LucideProps interface
  const iconProps: LucideProps = {
    size,
    color,
  };

  if (!Icon) {
    return null;
  }

  // Create the component from the imported module
  const IconComponent = Icon.default;
  return <IconComponent {...iconProps} />;
};

type PageIconSelectorProps = {
  value?: string;
  onChange?: (value: string) => void;
};

const PageIconSelector: React.FC<PageIconSelectorProps> = ({
  value,
  onChange,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  
  const selected = allIcons.find((icon) => icon.value === value);
  
  // Filter icons based on search query
  const filteredCategories = searchQuery
    ? [
        {
          category: "Search Results",
          icons: allIcons.filter((icon) =>
            icon.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
            icon.value.toLowerCase().includes(searchQuery.toLowerCase())
          ),
        },
      ]
    : iconCategories;

  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium mb-1">Navbar icon</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-64">
          <span className="flex items-center gap-2">
            <SelectValue placeholder="Select an icon">
              {value && value !== "none" ? (
                <span className="flex items-center gap-2">
                  <DynamicLucideIcon name={value} size={18} />
                  {selected?.label}
                </span>
              ) : (
                "None"
              )}
            </SelectValue>
          </span>
        </SelectTrigger>
        <SelectContent className="max-h-96 overflow-hidden">
          <div className="px-2 py-2 sticky top-0 bg-background z-10 border-b">
            <Input
              placeholder="Search icons..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8"
            />
          </div>
          <div className="overflow-y-auto max-h-80 px-1 py-1">
            <SelectItem value="none" className="flex items-center gap-2">
              None
            </SelectItem>
            {filteredCategories.map((category) => (
              <SelectGroup key={category.category}>
                <SelectLabel className="text-xs font-semibold text-muted-foreground px-2 py-1">
                  {category.category}
                </SelectLabel>
                {category.icons.map((icon) => (
                  <SelectItem
                    key={icon.value}
                    value={icon.value}
                    className="flex items-center gap-2 pl-4"
                  >
                    <span className="flex items-center gap-2">
                      <DynamicLucideIcon name={icon.value} size={16} />
                      {icon.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </div>
        </SelectContent>
      </Select>
    </div>
  );
};

export default PageIconSelector;
